<script>
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher();
  
  export let type = 'button'; // button, submit, reset
  export let variant = 'primary'; // primary, secondary, danger, success, warning, info, outline, ghost, link
  export let size = 'md'; // xs, sm, md, lg, xl
  export let disabled = false;
  export let loading = false;
  export let fullWidth = false;
  export let icon = null;
  export let iconPosition = 'left'; // left, right
  export let href = null; // 링크 버튼인 경우
  export let target = null; // 링크 타겟

  function handleClick(event) {
    if (disabled || loading) {
      event.preventDefault();
      return;
    }
    dispatch('click', event);
  }

  $: Tag = href ? 'a' : 'button';
  $: buttonClass = [
    'btn',
    variant.includes('outline') ? `btn-outline btn-${variant.replace('outline-', '')}` : `btn-${variant}`,
    `btn-${size}`,
    fullWidth && 'btn-block',
    disabled && 'btn-disabled',
    loading && 'btn-loading'
  ].filter(Boolean).join(' ');
</script>

<svelte:element 
  this={Tag}
  class={buttonClass}
  {type}
  {disabled}
  {href}
  {target}
  role={href ? 'link' : 'button'}
  aria-disabled={disabled || loading}
  tabindex={disabled ? -1 : 0}
  on:click={handleClick}
  {...$$restProps}
>
  {#if loading}
    <span class="btn-spinner" aria-hidden="true"></span>
    <span class="btn-text" style="opacity: 0;">
      <slot />
    </span>
  {:else}
    {#if icon && iconPosition === 'left'}
      <span class="btn-icon btn-icon-left" aria-hidden="true">
        {#if typeof icon === 'string'}
          {icon}
        {:else}
          {@html icon}
        {/if}
      </span>
    {/if}
    
    {#if $$slots.default}
      <span class="btn-text">
        <slot />
      </span>
    {/if}
    
    {#if icon && iconPosition === 'right'}
      <span class="btn-icon btn-icon-right" aria-hidden="true">
        {#if typeof icon === 'string'}
          {icon}
        {:else}
          {@html icon}
        {/if}
      </span>
    {/if}
  {/if}
</svelte:element>

<style>
  /* 버튼 아이콘 스타일 */
  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .btn-icon-left {
    margin-right: 8px;
  }

  .btn-icon-right {
    margin-left: 8px;
  }

  /* 로딩 스피너 스타일 */
  .btn-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: btn-spin 1s linear infinite;
    margin-right: 8px;
  }

  @keyframes btn-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* 텍스트 간격 조정 */
  .btn-text {
    display: inline-block;
  }

  /* 아이콘만 있는 버튼 처리 */
  :global(.btn:not(:has(.btn-text))) .btn-icon {
    margin: 0;
  }

  /* 접근성 향상 */
  @media (prefers-reduced-motion: reduce) {
    .btn-spinner {
      animation: none;
      border: 2px solid currentColor;
    }
  }
</style> 